/**
 * Public Prayer Request submission page.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Heart, CheckCircle2 } from "lucide-react";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

export default function PublicChurchPrayer() {
  const { slug } = useParams();
  const [church, setChurch] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", request: "", is_anonymous: false, is_urgent: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/churches/${slug}`);
        setChurch(data.church);
      } catch { /* */ }
    })();
  }, [slug]);

  const submit = async () => {
    if (!form.name.trim() || form.request.trim().length < 4) return toast.error("Name and a prayer request are required");
    setSending(true);
    try {
      await api.post(`/churches/${slug}/prayer-requests`, form);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="public-prayer-done" className="max-w-xl mx-auto px-6 py-20 text-center text-white">
        <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
        <h1 className="font-serif-display text-4xl mt-5">We&apos;re praying with you</h1>
        <p className="text-neutral-300 mt-3 text-sm">Your request has been received by {church?.name || slug}.</p>
        <Link to={`/churches/${slug}`} className="btn-gold mt-8 inline-flex px-5 py-2.5 rounded text-xs tracking-widest uppercase font-semibold">Back to site</Link>
      </div>
    );
  }

  return (
    <div data-testid="public-prayer" className="max-w-2xl mx-auto px-6 py-16 text-white">
      <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Prayer</div>
      <h1 className="font-serif-display text-4xl mt-2">Submit a request to <span className="italic text-[#D4AF37]">{church?.name || slug}</span></h1>
      <p className="text-neutral-300 mt-3 text-sm">Your request is private and only seen by the pastoral team.</p>

      <div className="mt-8 luxury-card rounded-xl p-6 space-y-4">
        <input data-testid="prayer-name" className={inp} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="prayer-email" className={inp} placeholder="Email (optional)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className={inp} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <textarea data-testid="prayer-request" className={inp} rows={6} placeholder="How can we pray for you?" value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} />
        <div className="flex flex-wrap gap-5 text-xs text-neutral-300">
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.is_anonymous} onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })} /> Submit anonymously</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.is_urgent} onChange={(e) => setForm({ ...form, is_urgent: e.target.checked })} /> Mark as urgent</label>
        </div>
        <button data-testid="prayer-submit" disabled={sending} onClick={submit} className="btn-gold px-6 py-3 rounded text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} />} Send request
        </button>
      </div>
    </div>
  );
}
